import type { IComponent } from '../types';

const escapeColumnsInProps = (componentProps: any): any => {
  if (!componentProps) return componentProps;

  const result = { ...componentProps };

  if (result.columns) {
    const escapeCol = (col: any) =>
      col.customComponentSchema
        ? {
            ...col,
            customComponentSchema: JSON.stringify(col.customComponentSchema),
          }
        : col;

    if (Array.isArray(result.columns)) {
      result.columns = result.columns.map(escapeCol);
    } else {
      result.columns = Object.fromEntries(
        Object.entries(result.columns).map(([key, col]) => [
          key,
          escapeCol(col),
        ])
      );
    }
  }

  if (
    result.childSchema?.properties?.childSchemaForm &&
    typeof result.childSchema.properties.childSchemaForm !== 'string'
  ) {
    result.childSchema = {
      ...result.childSchema,
      properties: {
        ...result.childSchema.properties,
        childSchemaForm: JSON.stringify(
          result.childSchema.properties.childSchemaForm
        ),
      },
    };
  }

  if (
    result.customComponentSchema &&
    typeof result.customComponentSchema !== 'string'
  ) {
    result.customComponentSchema = JSON.stringify(result.customComponentSchema);
  }

  // Each child collection carries its own row form and card schema, rendered by
  // the component itself (RecursionNode) with `$item` scoped to the row. Same
  // reason as the FlowChart schemas below: keep them opaque to Formily.
  if (Array.isArray(result.childCollections)) {
    result.childCollections = result.childCollections.map((collection: any) => {
      const escaped = { ...collection };
      for (const key of ['formSchema', 'cardSchema']) {
        if (escaped[key] && typeof escaped[key] !== 'string') {
          escaped[key] = JSON.stringify(escaped[key]);
        }
      }
      return escaped;
    });
  }

  // FlowChart nested schemas are row/node-scoped ($row/$node) and are rendered
  // by the component itself (FlowNode), not by Formily. Stringify them
  // so Formily's schema compiler treats them as opaque and never evaluates their
  // bindings (which would throw `$row is not defined`). Parsed back in FormFlowChart.
  for (const key of ['panelSchema', 'nodeCardSchema', 'nodeCardByType']) {
    if (result[key] && typeof result[key] !== 'string') {
      result[key] = JSON.stringify(result[key]);
    }
  }

  return result;
};

export const escapeFormItemsSchemas = (
  schema: IComponent<any>
): IComponent<any> => {
  if (!schema || typeof schema !== 'object') return schema;

  const result = { ...schema };

  if (result['x-component-props']) {
    result['x-component-props'] = escapeColumnsInProps(
      result['x-component-props']
    );
  }

  if (result.properties) {
    result.properties = Object.fromEntries(
      Object.entries(result.properties).map(([key, value]) => [
        key,
        escapeFormItemsSchemas(value as IComponent<any>),
      ])
    );
  }

  return result;
};
